import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { adminApi } from './adminApi';

const styles = `
  .services-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }
  .service-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 20px 22px;
    display: flex;
    align-items: flex-start;
    gap: 14px;
  }
  .service-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    margin-top: 3px;
    flex-shrink: 0;
  }
  .dot-ok { background: #27AE60; }
  .dot-warn { background: #F39C12; }
  .dot-error { background: #E74C3C; }
  .service-info {}
  .service-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #2C2C2C;
    margin-bottom: 3px;
  }
  .service-detail {
    font-size: 0.8rem;
    color: #888;
  }
  .service-status-ok { color: #27AE60; font-size: 0.8rem; font-weight: 600; }
  .service-status-warn { color: #F39C12; font-size: 0.8rem; font-weight: 600; }
  .service-status-error { color: #E74C3C; font-size: 0.8rem; font-weight: 600; }
  .section-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #2C2C2C;
    margin-bottom: 14px;
    letter-spacing: -0.01em;
  }
  .log-box {
    background: #1A1A1A;
    border-radius: 12px;
    padding: 20px 24px;
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 0.78rem;
    color: #A8C8A8;
    line-height: 1.7;
    max-height: 400px;
    overflow-y: auto;
    box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  }
  .log-line { white-space: pre-wrap; word-break: break-all; }
  .log-empty { color: #666; font-style: italic; }
  .btn-refresh {
    background: none;
    border: 1.5px solid #E0DDD8;
    color: #666;
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    margin-bottom: 20px;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .btn-refresh:hover { background: #FFF5F0; color: #E8845A; border-color: #E8845A; }
  .loading-state { color: #888; font-size: 0.9rem; padding: 20px 0; }
  .error-state { color: #C0392B; font-size: 0.9rem; padding: 20px 0; }
  .voice-settings-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    padding: 24px 28px;
    margin-bottom: 32px;
    max-width: 420px;
  }
  .voice-settings-card label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #2C2C2C;
    margin-bottom: 6px;
  }
  .voice-settings-card input[type="number"] {
    width: 120px;
    padding: 8px 10px;
    border: 1.5px solid #E0DDD8;
    border-radius: 8px;
    font-size: 0.9rem;
    font-family: inherit;
  }
  .voice-settings-card .default-hint {
    font-size: 0.78rem;
    color: #999;
    margin-top: 4px;
  }
  .voice-settings-card .tip {
    font-size: 0.78rem;
    color: #888;
    margin-top: 8px;
    line-height: 1.5;
  }
  .btn-save {
    background: #E8845A;
    color: #fff;
    border: none;
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 9px 20px;
    border-radius: 8px;
    cursor: pointer;
    margin-top: 14px;
    transition: opacity 0.15s;
  }
  .btn-save:hover { opacity: 0.85; }
  .btn-save:disabled { opacity: 0.5; cursor: default; }
  .save-ok { color: #27AE60; font-size: 0.8rem; margin-left: 10px; }
  .save-err { color: #E74C3C; font-size: 0.8rem; margin-left: 10px; }
`;

export default function AdminSystem() {
  const [health, setHealth] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Voice settings state
  const [vadSilenceMs, setVadSilenceMs] = useState(1200);
  const [vadStrategy, setVadStrategy] = useState('client_vad');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // { ok: bool, text: string }

  // Log filter state — defaults match the SSH-grep view I use to debug
  // (excludes polling endpoints; INFO+ shown).
  const [logLevel, setLogLevel] = useState('');
  const [logShowPolling, setLogShowPolling] = useState(false);
  const [logLimit, setLogLimit] = useState(200);

  async function loadData() {
    try {
      const [h, l, cfg] = await Promise.all([
        adminApi.systemHealth(),
        adminApi.systemLogs({ limit: logLimit, level: logLevel, showPolling: logShowPolling }),
        adminApi.getPublicConfig(),
      ]);
      setHealth(h);
      setLogs(l);
      if (cfg && cfg.vad_silence_ms) setVadSilenceMs(cfg.vad_silence_ms);
      if (cfg && cfg.vad_strategy) setVadStrategy(cfg.vad_strategy);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    loadData().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload logs only (cheap) when filter knobs change.
  useEffect(() => {
    if (loading) return;
    adminApi
      .systemLogs({ limit: logLimit, level: logLevel, showPolling: logShowPolling })
      .then(setLogs)
      .catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logLevel, logShowPolling, logLimit]);

  async function refresh() {
    setRefreshing(true);
    setError('');
    await loadData();
    setRefreshing(false);
  }

  async function saveVoiceSettings() {
    setSaving(true);
    setSaveMsg(null);
    try {
      await adminApi.updateConfig({
        vad_silence_ms: vadSilenceMs,
        vad_strategy: vadStrategy,
      });
      setSaveMsg({ ok: true, text: 'Saved — refresh tablet to apply' });
    } catch (e) {
      setSaveMsg({ ok: false, text: e.message });
    }
    setSaving(false);
  }

  const dotClass = (status) => {
    if (status === 'ok') return 'dot-ok';
    if (status === 'warn') return 'dot-warn';
    return 'dot-error';
  };
  const statusClass = (status) => {
    if (status === 'ok') return 'service-status-ok';
    if (status === 'warn') return 'service-status-warn';
    return 'service-status-error';
  };

  return (
    <AdminLayout title="System Health">
      <style>{styles}</style>

      <button className="btn-refresh" onClick={refresh} disabled={refreshing}>
        {refreshing ? 'Refreshing…' : '⟳ Refresh'}
      </button>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {!loading && (
        <>
          <div className="section-title">Voice Settings</div>
          <div className="voice-settings-card">
            <label htmlFor="vad-strategy">End-of-speech detection</label>
            <select
              id="vad-strategy"
              value={vadStrategy}
              onChange={(e) => setVadStrategy(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1.5px solid #E0DDD8',
                borderRadius: 8,
                fontSize: '0.9rem',
                fontFamily: 'inherit',
                background: '#fff',
              }}
            >
              <option value="client_vad">Browser VAD (current — silence-based)</option>
              <option value="deepgram_endpointing">Deepgram endpointing (smart — speech-aware)</option>
              <option value="hybrid">Hybrid (whichever fires first)</option>
            </select>
            <div className="tip">
              <strong>Browser VAD:</strong> stops when audio is silent for the threshold below.
              Cuts users off if they pause mid-sentence.<br/>
              <strong>Deepgram endpointing:</strong> Deepgram decides when the utterance is done
              (uses speech context, not just amplitude). Smarter for natural pauses, ~50-100ms
              extra latency.<br/>
              <strong>Hybrid:</strong> stops on whichever signal arrives first.
            </div>

            <label htmlFor="vad-silence" style={{ marginTop: 18 }}>
              VAD silence timeout (ms) — used by Browser VAD / Hybrid
            </label>
            <input
              id="vad-silence"
              type="number"
              min={500}
              max={4000}
              step={100}
              value={vadSilenceMs}
              onChange={(e) => setVadSilenceMs(Number(e.target.value))}
            />
            <div className="default-hint">Default: 1200ms</div>
            <div className="tip">
              Increase for users who speak slowly or pause mid-sentence.
            </div>
            <div>
              <button
                className="btn-save"
                onClick={saveVoiceSettings}
                disabled={saving || vadSilenceMs < 500 || vadSilenceMs > 4000}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {saveMsg && (
                <span className={saveMsg.ok ? 'save-ok' : 'save-err'}>
                  {saveMsg.text}
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {health && (
        <>
          <div className="section-title">Services</div>
          <div className="services-grid">
            {health.services.map(s => (
              <div key={s.name} className="service-card">
                <div className={`service-dot ${dotClass(s.status)}`} />
                <div className="service-info">
                  <div className="service-name">{s.name}</div>
                  <div className={statusClass(s.status)}>
                    {s.status.toUpperCase()}
                  </div>
                  {s.detail && <div className="service-detail">{s.detail}</div>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {logs && (
        <>
          <div className="section-title" style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <span>Recent Logs</span>
            <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: 400 }}>
              {logs.total_buffered != null
                ? `${logs.entries?.length ?? 0} shown / ${logs.total_buffered} buffered`
                : ''}
            </span>
          </div>

          <div style={{
            display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>Level:</span>
              <select
                value={logLevel}
                onChange={(e) => setLogLevel(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '0.85rem', borderRadius: 6, border: '1px solid #ddd' }}
              >
                <option value="">All</option>
                <option value="INFO">INFO+</option>
                <option value="WARNING">WARNING+</option>
                <option value="ERROR">ERROR+</option>
              </select>
            </label>

            <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={logShowPolling}
                onChange={(e) => setLogShowPolling(e.target.checked)}
              />
              <span>Show polling endpoints</span>
            </label>

            <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>Limit:</span>
              <select
                value={logLimit}
                onChange={(e) => setLogLimit(Number(e.target.value))}
                style={{ padding: '4px 8px', fontSize: '0.85rem', borderRadius: 6, border: '1px solid #ddd' }}
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </label>
          </div>

          <div className="log-box">
            {logs.entries && logs.entries.length > 0 ? (
              logs.entries.map((entry, i) => {
                const tsShort = entry.ts ? entry.ts.slice(11, 19) : '';
                let levelColour = '#A8C8A8';
                if (entry.level === 'WARNING') levelColour = '#F39C12';
                else if (entry.level === 'ERROR' || entry.level === 'CRITICAL') levelColour = '#E74C3C';
                else if (entry.level === 'INFO') levelColour = '#888';
                return (
                  <div key={i} className="log-line" style={{ display: 'flex', gap: 10 }}>
                    <span style={{ color: '#666', flexShrink: 0 }}>{tsShort}</span>
                    <span style={{ color: levelColour, flexShrink: 0, fontWeight: 600, minWidth: 60 }}>
                      {entry.level}
                    </span>
                    <span style={{ wordBreak: 'break-all' }}>{entry.message}</span>
                  </div>
                );
              })
            ) : logs.lines && logs.lines.length > 0 ? (
              // Fallback if backend returns the legacy shape
              logs.lines.map((line, i) => (
                <div key={i} className="log-line">{line}</div>
              ))
            ) : (
              <div className="log-empty">No log lines available.</div>
            )}
          </div>
        </>
      )}
    </AdminLayout>
  );
}
